import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@devhop/ui";
import { RiArrowDownSLine, RiMore2Line } from "@remixicon/react";
import type { DragEvent, ReactNode } from "react";
import { Fragment } from "react";
import { AppImage } from "@/shared/ui/AppImage";

export type WorkspaceProductCardAction = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
  onSelect: () => void;
};

export type WorkspaceProductCardVariant = {
  id: string;
  selectionLabel: string;
  content: ReactNode;
  mobilePrice?: ReactNode;
  desktopPrice?: ReactNode;
  actions?: WorkspaceProductCardAction[];
};

type CheckedState = boolean | "indeterminate";

export function WorkspaceProductCard({
  productId,
  name,
  imageUrl,
  subtitle,
  price,
  badge,
  active = false,
  variants,
  selectedVariantIds,
  onSelectionChange,
  selectionDisabled = false,
  leading,
  actions = [],
  management,
  draggable = false,
  onDragStart,
  onDrop,
}: {
  productId: string;
  name: string;
  imageUrl?: string | null;
  subtitle: ReactNode;
  price?: ReactNode;
  badge?: ReactNode;
  active?: boolean;
  variants: WorkspaceProductCardVariant[];
  selectedVariantIds?: ReadonlySet<string>;
  onSelectionChange?: (variantIds: string[], checked: boolean) => void;
  selectionDisabled?: boolean;
  leading?: ReactNode;
  actions?: WorkspaceProductCardAction[];
  management?: ReactNode;
  draggable?: boolean;
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>) => void;
}) {
  const selectedCount = selectedVariantIds
    ? variants.filter((variant) => selectedVariantIds.has(variant.id)).length
    : 0;
  const checked: CheckedState =
    variants.length > 0 && selectedCount === variants.length
      ? true
      : selectedCount > 0
        ? "indeterminate"
        : false;

  const actionMenu = (items: WorkspaceProductCardAction[], label: string) => (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-11 shrink-0"
          aria-label={label}
          title="ການຈັດການ"
        >
          <RiMore2Line className="size-5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {items.map((action) => (
          <Fragment key={action.id}>
            {action.separatorBefore ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              disabled={action.disabled}
              className={
                action.destructive
                  ? "text-destructive focus:text-destructive"
                  : undefined
              }
              onSelect={action.onSelect}
            >
              {action.icon}
              {action.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <Collapsible defaultOpen={false} asChild>
      <Card
        className={`min-w-0 gap-0 py-0 ${active ? "border-primary" : ""}`}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={draggable ? (event) => event.preventDefault() : undefined}
        onDrop={onDrop}
      >
        <CardContent className="p-0">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 p-2">
            {onSelectionChange ? (
              <label
                className="flex size-11 shrink-0 items-center justify-center"
                htmlFor={`workspace-product-${productId}`}
              >
                <Checkbox
                  id={`workspace-product-${productId}`}
                  aria-label={`ເລືອກທຸກ SKU ຂອງ ${name}`}
                  disabled={selectionDisabled || variants.length === 0}
                  checked={checked}
                  onCheckedChange={(value) =>
                    onSelectionChange(
                      variants.map((variant) => variant.id),
                      value === true,
                    )
                  }
                />
              </label>
            ) : (
              <span className="flex size-11 shrink-0 items-center justify-center">
                {leading}
              </span>
            )}
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`ຕົວເລືອກຂອງ ${name}`}
              >
                <AppImage
                  src={imageUrl}
                  alt=""
                  fit="contain"
                  className="size-10 shrink-0 rounded-md"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-semibold text-sm">
                      {name}
                    </span>
                    {badge ? (
                      <Badge
                        variant="secondary"
                        className="shrink-0 px-1.5 py-0 text-[10px]"
                      >
                        {badge}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="block truncate text-muted-foreground text-xs">
                    {subtitle}
                  </span>
                  {price ? (
                    <span className="block text-xs tabular-nums">{price}</span>
                  ) : null}
                </span>
                <RiArrowDownSLine
                  className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
            {actions.length > 0 ? (
              actionMenu(actions, `ການຈັດການ ${name}`)
            ) : (
              <span className="size-11" />
            )}
          </div>
          {management}
          <CollapsibleContent className="divide-y border-t">
            {variants.map((variant) => (
              <div
                key={variant.id}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-1.5 px-2 py-1.5 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto]"
              >
                {onSelectionChange ? (
                  <label
                    htmlFor={`workspace-variant-${variant.id}`}
                    className="flex size-11 items-center justify-center"
                  >
                    <Checkbox
                      id={`workspace-variant-${variant.id}`}
                      aria-label={variant.selectionLabel}
                      checked={selectedVariantIds?.has(variant.id) ?? false}
                      disabled={selectionDisabled}
                      onCheckedChange={(value) =>
                        onSelectionChange([variant.id], value === true)
                      }
                    />
                  </label>
                ) : (
                  <span className="flex size-11 items-center justify-center">
                    {leading}
                  </span>
                )}
                <div className="min-w-0">
                  {variant.content}
                  {variant.mobilePrice ? (
                    <strong className="text-xs tabular-nums sm:hidden">
                      {variant.mobilePrice}
                    </strong>
                  ) : null}
                </div>
                <div className="hidden min-w-24 text-right sm:block">
                  {variant.desktopPrice}
                </div>
                {variant.actions?.length ? (
                  actionMenu(
                    variant.actions,
                    `ການຈັດການ ${variant.selectionLabel}`,
                  )
                ) : (
                  <span className="size-11" />
                )}
              </div>
            ))}
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}
